import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import { getConversations } from "@/utils/chatService";
import { Conversation } from "@/types/chat";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { UserAvatar } from "@/components/UserAvatar";

const Conversations = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  // Load conversations on mount and when user changes
  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  // Set up realtime subscription for new conversations and messages
  useEffect(() => {
    if (!user) return;
    
    // Subscribe to changes in conversations table
    const channel = supabase
      .channel('conversations-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `participant1_id.eq.${user.id},participant2_id.eq.${user.id}`
        },
        () => {
          // Reload conversations when there's a change
          loadConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        (payload) => {
          // Check if this message belongs to any of the user's conversations
          const messageData = payload.new as { conversation_id: string };
          if (messageData && messageData.conversation_id) {
            loadConversations();
          }
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const loadConversations = async () => {
    setIsLoading(true);
    try {
      if (user) {
        const result = await getConversations();
        setConversations(result);
      }
    } catch (error) {
      console.error("Failed to load conversations:", error);
      toast.error("Failed to load conversations. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/auth");
    }
  }, [user, navigate]);

  const handleConversationClick = (conversation: Conversation) => {
    // Navigate to the chat route instead of conversation detail
    navigate(`/chat/${conversation.convoId}`);
  };

  // Format the time as relative (e.g., "2 hours ago")
  const formatTime = (date: Date) => {
    return formatDistanceToNow(date, { addSuffix: true });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="flex-grow container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 text-foreground">Conversations</h1>
      <Card className="shadow-md w-full max-w-3xl mx-auto bg-card border-border">
        <ScrollArea className="h-[70vh]">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-muted-foreground">Loading conversations...</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex justify-center items-center h-full p-6">
              <div className="text-center">
                <p className="text-muted-foreground mb-2">No conversations yet</p>
                <p className="text-sm text-muted-foreground">
                  Start chatting with other users to see conversations here.
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {conversations.map((conversation) => (
                <div
                  key={conversation.convoId}
                  className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleConversationClick(conversation)}
                >
                  <div className="flex items-center gap-3">
                    <UserAvatar 
                      userId={conversation.otherUserId}
                      avatarUrl={conversation.otherUserAvatarUrl}
                      fallback={conversation.otherUserInitials}
                      size="lg"
                      showLevel={false}
                    />
                    <div className="flex-grow">
                      <div className="flex justify-between items-baseline">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-foreground">{conversation.otherUserName}</h3>
                          {conversation.serviceTitle && (
                            <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                              {conversation.serviceTitle}
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatTime(conversation.lastMessageTime)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate max-w-xs md:max-w-lg">
                        {conversation.lastMessagePreview}
                      </p>
                    </div>
                    {conversation.unreadCount && (
                      <div className="bg-primary text-primary-foreground rounded-full h-5 w-5 flex items-center justify-center text-xs">
                        {conversation.unreadCount}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
};

export default Conversations;
